/*
 * Назначение: провайдер авторизации админки.
 * Функционал: при загрузке зовёт /me, хранит пользователя и состояние,
 * отдаёт login/logout наружу. Слушает событие "auth:unauthorized" из
 * api-клиента и сбрасывает пользователя — это переводит UI на /login.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ApiError, AUTH_UNAUTHORIZED_EVENT } from "../../../lib/api";
import { authApi, type AdminUser, type LoginResult } from "../../../lib/auth";

type AuthStatus = "loading" | "anonymous" | "authenticated";

interface AuthContextValue {
  status: AuthStatus;
  user: AdminUser | null;
  /** Пароль. Если вернулось twofa_required — вход не завершён, нужен код. */
  login: (loginValue: string, password: string) => Promise<LoginResult>;
  /** Второй шаг: код из письма. */
  confirmCode: (
    challengeId: string,
    code: string,
    rememberDevice: boolean,
  ) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: AdminUser) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const me = await authApi.me();
      if (!mountedRef.current) return;
      setUser(me);
      setStatus("authenticated");
    } catch (err) {
      if (!mountedRef.current) return;
      setUser(null);
      setStatus("anonymous");
      if (!(err instanceof ApiError) || err.status !== 401) {
        // 401 — нормальный кейс «не залогинен», шумные ошибки логируем
        console.error("auth/me_неожиданная_ошибка", err);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void refresh();

    const onUnauthorized = () => {
      if (!mountedRef.current) return;
      setUser(null);
      setStatus("anonymous");
    };
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);

    return () => {
      mountedRef.current = false;
      window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
    };
  }, [refresh]);

  const login = useCallback(async (loginValue: string, password: string) => {
    const result = await authApi.login(loginValue, password);
    // Сессия выдана только если код не требуется.
    if (!result.twofa_required && result.user) {
      setUser(result.user);
      setStatus("authenticated");
    }
    return result;
  }, []);

  const confirmCode = useCallback(
    async (challengeId: string, code: string, rememberDevice: boolean) => {
      const result = await authApi.login2fa(challengeId, code, rememberDevice);
      if (result.user) {
        setUser(result.user);
        setStatus("authenticated");
      }
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
      setStatus("anonymous");
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, login, confirmCode, logout, setUser }),
    [status, user, login, confirmCode, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth должен использоваться внутри AuthProvider");
  }
  return ctx;
}
